/**
 * Public Landing Page — M10.5 Cinematic Refinement.
 *
 * The visitor's first impression of Orkexra. Every animation is
 * purposeful. Every gradient earns its weight. Every headline lands
 * before the eye moves.
 *
 * Direction: Apple × JARVIS × Stripe × Linear — cinematic, alive,
 * intelligent, warm, executive, inevitable. No neon, no gaming, no
 * gimmicks.
 *
 * Authenticated routes remain untouched. This page is fully public.
 */
import { motion, useInView } from "framer-motion";
import {
  Archive, BookOpen, Calendar, CheckCircle2, ChevronRight, Coffee, Compass,
  FileText, Film, Globe, ImageIcon, Layers, Mail, Megaphone, MessageSquare, Palette, PenLine, Play, Rss, RotateCcw,
  Shield, ShieldCheck, Sparkles, Star, Target, User, Video, Youtube,
} from "lucide-react";

/** Executive demo booking link. Replace with the live Calendly URL when scheduled. */
const CALENDLY_URL = "https://calendly.com/orkexra/executive-demo";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { OrkexraLogo } from "@/shared/ui/OrkexraLogo";

/* ── Helpers ─────────────────────────────────────────────────── */
function useAnimatedInView<T extends HTMLElement>(margin = "0px 0px -80px 0px") {
  const ref = useRef<T>(null);
  const inView = useInView(ref, { once: true, margin: margin as any });
  return { ref, inView };
}

export function CountUp({ to, duration = 1.2, prefix = "", suffix = "", trigger }: {
  to: number; duration?: number; prefix?: string; suffix?: string; trigger?: boolean;
}) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (trigger === false) return;
    const start = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, trigger]);
  return <>{prefix}{n.toLocaleString()}{suffix}</>;
}

/* ── Aurora background — cinematic lighting layer ─────────────── */
function Aurora(): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div
        className="absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(229,180,84,0.14), transparent 60%)", animation: "md-aurora 22s ease-in-out infinite alternate" }}
      />
      <div
        className="absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(74,222,128,0.10), transparent 60%)", animation: "md-aurora 26s ease-in-out infinite alternate-reverse" }}
      />
      <div
        className="absolute bottom-0 left-1/3 h-[600px] w-[600px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(180,83,9,0.10), transparent 60%)", animation: "md-aurora 34s ease-in-out infinite alternate" }}
      />
      {/* faint noise */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.035]">
        <filter id="n"><feTurbulence baseFrequency="0.9" numOctaves="2" /></filter>
        <rect width="100%" height="100%" filter="url(#n)" />
      </svg>
    </div>
  );
}

/* ── Public Nav ──────────────────────────────────────────────── */
function PublicNav(): JSX.Element {
  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--md-line)] backdrop-blur-xl"
      style={{ background: "linear-gradient(180deg, rgba(15,18,24,0.82), rgba(15,18,24,0.4))" }}>
      <div className="mx-auto flex max-w-[1280px] items-center gap-6 px-6 py-4">
        <Link to="/welcome" className="flex items-center gap-3">
          <OrkexraLogo variant="wordmark" size={30} />
        </Link>
        <nav className="hidden flex-1 items-center justify-center gap-8 md:flex">
          {[
            { label: "Platform",  href: "#platform",  to: null },
            { label: "Solutions", href: "#solutions", to: null },
            { label: "Resources", href: "#resources", to: null },
            { label: "Pricing",   href: null,         to: "/pricing" as const },
            { label: "About",     href: "#about",     to: null },
          ].map((item) => (
            item.to ? (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="rounded text-sm text-[var(--md-fog)] transition hover:text-[var(--md-ivory-0)] focus-visible:text-[var(--md-ivory-0)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--md-amber)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--md-ink-0)]"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href ?? "#"}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="rounded text-sm text-[var(--md-fog)] transition hover:text-[var(--md-ivory-0)] focus-visible:text-[var(--md-ivory-0)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--md-amber)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--md-ink-0)]"
              >
                {item.label}
              </a>
            )
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <Link to="/login" data-testid="landing-signin" className="md-btn md-btn--ghost inline-flex whitespace-nowrap">
            <span>Sign in</span>
          </Link>
          <Link
            to="/demo"
            data-testid="landing-interactive-demo"
            className="md-btn md-btn--primary whitespace-nowrap"
          >
            <Play className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Try the Demo</span>
            <span className="sm:hidden">Demo</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ── Section 1: Hero ─────────────────────────────────────────── */
function Hero(): JSX.Element {
  return (
    <section className="relative overflow-hidden px-6 pt-20 pb-8 md:pt-32 md:pb-12">
      <div className="mx-auto max-w-[1280px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.2, 0.7, 0.3, 1] }}
          className="mx-auto max-w-4xl text-center"
        >
          {/* Patch 36.1 — official Orkexra lockup. The 'Powered by
              Akinex' attribution is baked into the lockup raster so we
              do not add a duplicated live eyebrow here. */}
          <div className="mx-auto mb-6 flex justify-center" data-testid="landing-hero-lockup">
            <OrkexraLogo variant="lockup" size={220} />
          </div>
          <h1 className="mt-6 font-brand tracking-tight text-[var(--md-ivory-0)] text-[44px] leading-[1.05] md:text-[76px] lg:text-[88px]">
            Orchestrate. Automate.{" "}
            <span className="brand-gradient-text">Grow.</span>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-[17px] leading-relaxed text-[var(--md-fog)] md:text-lg">
            Tell us what you want to accomplish. Orkexra builds the strategy, creates the campaign, prepares the schedule, and keeps your marketing moving — according to your approval settings.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/demo/workspace"
              data-testid="hero-cta-primary"
              className="md-btn md-btn--primary"
              style={{ padding: "14px 22px", fontSize: 14 }}
            >
              Explore Ready-to-Work Demo <ChevronRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/demo/brain-builder"
              data-testid="hero-cta-secondary"
              className="md-btn md-btn--ghost"
              style={{ padding: "14px 22px", fontSize: 14 }}
            >
              <Play className="h-3.5 w-3.5" /> See How It Works
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[var(--md-amber)] text-[var(--md-amber)]" />)}
              <span className="ml-1 text-xs text-[var(--md-fog)]">Built for business owners</span>
            </div>
            {["Strategy · Content · Editorial · Publishing", "Trust-based Autopilot", "You approve. We work.", "SOC 2 · GDPR"].map((l) => (
              <div key={l} className="flex items-center gap-1.5 md-eyebrow">
                <Shield className="h-3 w-3 text-[var(--md-amber)]" />{l}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Product preview — floats below hero, not competing */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.2, 0.7, 0.3, 1] }}
          className="relative mx-auto mt-20 max-w-6xl"
        >
          <div aria-hidden className="pointer-events-none absolute -inset-4 -z-10 rounded-[28px] opacity-70 blur-2xl"
               style={{ background: "radial-gradient(600px 220px at 50% 30%, rgba(229,180,84,0.22), transparent 70%)" }} />
          <HeroDashboardPreview />
        </motion.div>
      </div>
    </section>
  );
}

/* Hero dashboard — cinematic Mission Control.
   Patch 17 · The department at work.
   No AI activity feed. No "AI saved". No tokens. Just the department
   quietly executing eleven kinds of work at once. */
function HeroDashboardPreview(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>("-50px");

  // The eleven work items the department is running right now.
  // Order intentionally alternates status so the panel feels alive.
  const workItems: Array<{
    channel: string;
    kind: string;
    title: string;
    status: "In progress" | "Ready" | "Scheduled" | "Awaiting approval";
    progress?: number; // 0-100 for In progress
    icon: JSX.Element;
  }> = [
    { channel: "STRATEGY",  kind: "Q4 plan",         title: "Autumn campaign strategy",     status: "In progress",       progress: 68, icon: <Compass className="h-3 w-3" /> },
    { channel: "BLOG",      kind: "Article",         title: "The story behind our roast",   status: "In progress",       progress: 42, icon: <BookOpen className="h-3 w-3" /> },
    { channel: "VIDEO",     kind: "Reel",            title: "Behind the counter",           status: "In progress",       progress: 81, icon: <Film className="h-3 w-3" /> },
    { channel: "CREATIVE",  kind: "Image",           title: "Fall menu hero shot",          status: "Ready",             icon: <ImageIcon className="h-3 w-3" /> },
    { channel: "EMAIL",     kind: "Newsletter",      title: "Weekly customer update",       status: "Ready",             icon: <Mail className="h-3 w-3" /> },
    { channel: "FACEBOOK",  kind: "Post",            title: "Community weekend feature",    status: "Scheduled",         icon: <Rss className="h-3 w-3" /> },
    { channel: "INSTAGRAM", kind: "Post",            title: "Origin story · carousel",      status: "Scheduled",         icon: <ImageIcon className="h-3 w-3" /> },
    { channel: "TIKTOK",    kind: "Short",           title: "Latte pour · 15s",             status: "In progress",       progress: 55, icon: <Film className="h-3 w-3" /> },
    { channel: "YOUTUBE",   kind: "Short",           title: "Meet the roaster · teaser",    status: "In progress",       progress: 22, icon: <Play className="h-3 w-3" /> },
    { channel: "GBP",       kind: "Update",          title: "Autumn hours & new photos",    status: "Ready",             icon: <Star className="h-3 w-3" /> },
    { channel: "APPROVAL",  kind: "Awaiting review", title: "Subscribe-and-save campaign",  status: "Awaiting approval", icon: <CheckCircle2 className="h-3 w-3" /> },
  ];

  const statusMeta: Record<typeof workItems[number]["status"], { color: string; bg: string; ring: string }> = {
    "In progress":       { color: "#F5C15C", bg: "rgba(229,180,84,0.10)",  ring: "rgba(229,180,84,0.35)" },
    "Ready":             { color: "#7EE7A3", bg: "rgba(74,222,128,0.10)",  ring: "rgba(74,222,128,0.35)" },
    "Scheduled":         { color: "#8FCEF5", bg: "rgba(125,211,252,0.10)", ring: "rgba(125,211,252,0.35)" },
    "Awaiting approval": { color: "#F0B98A", bg: "rgba(240,185,138,0.10)", ring: "rgba(240,185,138,0.40)" },
  };

  return (
    <div>
      {/* Patch 18 · Transparent labeling — visitors should know this is an
          illustrative example, not real customer data. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <span
            data-testid="landing-hero-preview-label"
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-[var(--md-amber)]"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--md-amber)]" />
            Live product preview
          </span>
          <span className="text-[11px] leading-relaxed text-[var(--md-fog)]">
            An illustrative example of how your Marketing Department coordinates active work.
          </span>
        </div>
      </div>
      <div
        ref={ref}
        data-testid="landing-hero-preview"
        className="relative overflow-hidden rounded-2xl border border-[color:var(--md-amber)]/25"
        style={{
          background: "linear-gradient(180deg, rgba(20,24,33,0.94), rgba(15,18,24,0.85))",
          boxShadow: "0 60px 140px -50px rgba(0,0,0,0.75), 0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(229,180,84,0.10)",
        }}
      >
      {/* App chrome — mirrors Harmony's five-item nav */}
      <div className="flex items-center gap-2 border-b border-[color:var(--md-line)] px-4 py-3">
        <div className="grid h-6 w-6 place-items-center rounded-md" style={{ background: "linear-gradient(135deg, var(--md-amber), #F5C15C)" }}>
          <span className="font-display text-[11px] italic text-[var(--md-ink-0)]">m</span>
        </div>
        <b className="text-[12px] font-semibold tracking-wide">Orkexra</b>
        <nav className="ml-4 flex gap-1 text-[11px] text-[var(--md-fog)]">
          <span className="rounded-md bg-[var(--md-ink-3)] px-2 py-1 text-[var(--md-ivory-0)]" style={{ boxShadow: "inset 0 -1px 0 var(--md-amber)" }}>Home</span>
          {["Campaigns", "Content Vault", "Publishing", "Workspace"].map((n) => (
            <span key={n} className="px-2 py-1">{n}</span>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1.5 rounded-full border border-[color:var(--md-good)]/30 bg-[var(--md-good)]/8 px-2 py-1 text-[10px] text-[var(--md-good)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--md-good)]" />
            Autopilot · Assistant
          </span>
          <div className="grid h-6 w-6 place-items-center rounded-full" style={{ background: "linear-gradient(135deg, #B45309, #F59E0B)" }} />
        </div>
      </div>

      {/* Body: left = Morning brief · center = Department at work · right = Publishing queue */}
      <div className="grid gap-4 p-5 lg:grid-cols-[0.9fr_1.35fr_1fr]">
        {/* Morning brief — the "what an executive would want to know" panel */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }}
          className="md-panel rounded-xl p-4"
        >
          <div className="md-eyebrow md-eyebrow--amber md-eyebrow--live">Your morning</div>
          <div className="mt-3 font-display text-[15px] italic leading-snug text-[var(--md-ivory-0)]">
            Good morning.
          </div>
          <div className="mt-1 text-[11px] text-[var(--md-fog)]">
            Yesterday your Marketing Department&hellip;
          </div>
          <ul className="mt-3 space-y-1.5 text-[12px]">
            {[
              "Published three videos",
              "Scheduled seven social posts",
              "Finished two blogs",
              "Prepared one newsletter",
              "Updated Google Business",
            ].map((l, i) => (
              <motion.li key={l}
                initial={{ opacity: 0, x: -6 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.35, delay: 0.35 + i * 0.08 }}
                className="flex items-center gap-2 text-[var(--md-ivory-0)]"
              >
                <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-[var(--md-good)]" /> {l}
              </motion.li>
            ))}
            <motion.li
              initial={{ opacity: 0, x: -6 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.35, delay: 0.75 }}
              className="flex items-center gap-2 text-[var(--md-ivory-0)]"
            >
              <span className="grid h-3 w-3 flex-shrink-0 place-items-center rounded-full border border-[color:var(--md-amber)]/60">
                <span className="h-1 w-1 rounded-full bg-[var(--md-amber)]" />
              </span>
              One campaign awaiting approval
            </motion.li>
          </ul>
          <div className="mt-4 rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)] p-2 text-[11px] leading-relaxed text-[var(--md-fog)]">
            No action required today.
          </div>
        </motion.div>

        {/* Department at work — 11 live cards */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.28 }}
          className="md-panel rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div className="md-eyebrow">Marketing Department · working now</div>
            <span className="md-eyebrow md-eyebrow--live md-eyebrow--amber">Live</span>
          </div>
          <ul className="mt-3 grid gap-1.5">
            {workItems.map((r, i) => {
              const m = statusMeta[r.status];
              return (
                <motion.li key={`${r.channel}-${r.title}`}
                  initial={{ opacity: 0, y: 6 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.3, delay: 0.4 + i * 0.05 }}
                  className="grid grid-cols-[68px_1fr_auto] items-center gap-2 rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-2 py-1.5"
                >
                  <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-[var(--md-fog)]">
                    {r.icon}
                    {r.channel}
                  </span>
                  <span className="min-w-0 truncate text-[11px] text-[var(--md-ivory-0)]" title={r.title}>{r.title}</span>
                  <span
                    className="whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                    style={{ background: m.bg, color: m.color, boxShadow: `inset 0 0 0 1px ${m.ring}` }}
                  >
                    {r.status === "In progress" && typeof r.progress === "number"
                      ? `${r.progress}%`
                      : r.status}
                  </span>
                </motion.li>
              );
            })}
          </ul>
          <div className="mt-3 flex items-center justify-between text-[11px] text-[var(--md-fog)]">
            <span>11 pieces of work in flight</span>
            <span className="text-[var(--md-amber)]">You&rsquo;ll be pinged only when needed.</span>
          </div>
        </motion.div>

        {/* Publishing queue — the plan is ready */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.36 }}
          className="md-panel rounded-xl p-4"
        >
          <div className="md-eyebrow">This week&rsquo;s publishing</div>
          <ul className="mt-3 space-y-2 text-[11px]">
            {[
              { day: "Mon", ch: "IG",    title: "Story of the week",   done: true  },
              { day: "Tue", ch: "LI",    title: "Founder's note",      done: true  },
              { day: "Wed", ch: "EMAIL", title: "Weekly update",       done: false },
              { day: "Thu", ch: "IG",    title: "Customer spotlight",  done: false },
              { day: "Fri", ch: "TT",    title: "Behind the scenes",   done: false },
            ].map((row, i) => (
              <motion.li key={`${row.day}-${row.ch}`}
                initial={{ opacity: 0, y: 4 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.3, delay: 0.55 + i * 0.06 }}
                className="grid grid-cols-[36px_44px_1fr_auto] items-center gap-2"
              >
                <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--md-fog)]">{row.day}</span>
                <span
                  className="rounded px-1.5 py-0.5 text-center font-mono text-[9px] uppercase tracking-widest"
                  style={{ background: "rgba(229,180,84,0.10)", color: "var(--md-amber)" }}
                >
                  {row.ch}
                </span>
                <span className="min-w-0 truncate text-[var(--md-ivory-0)]">{row.title}</span>
                {row.done ? (
                  <CheckCircle2 className="h-3 w-3 text-[var(--md-good)]" />
                ) : (
                  <span className="h-2 w-2 rounded-full border border-[color:var(--md-amber)]/60" />
                )}
              </motion.li>
            ))}
          </ul>
          <div className="mt-4 border-t border-[color:var(--md-line)] pt-3 text-[10px] leading-relaxed text-[var(--md-fog)]">
            <span className="font-mono uppercase tracking-widest text-[var(--md-amber)]">Ready</span> — publishing connects to your platforms when you&rsquo;re ready.
          </div>
        </motion.div>
      </div>

      {/* Bottom rail — one-glance departmental status */}
      <div className="grid gap-3 border-t border-[color:var(--md-line)] px-5 py-4 md:grid-cols-6">
        {[
          { l: "Strategy",    v: "Prepared"    },
          { l: "Copywriting", v: "3 in flight" },
          { l: "Creative",    v: "2 ready"     },
          { l: "Video",       v: "3 rendering" },
          { l: "Publishing",  v: "5 queued"    },
          { l: "Autopilot",   v: "Assistant"   },
        ].map((t, i) => (
          <motion.div key={t.l}
            initial={{ opacity: 0, y: 6 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.35, delay: 0.85 + i * 0.05 }}
            className="md-panel md-panel--hover rounded-xl p-3"
          >
            <div className="md-eyebrow">{t.l}</div>
            <div className="mt-1 text-[13px] font-semibold text-[var(--md-ivory-0)]">{t.v}</div>
          </motion.div>
        ))}
      </div>
      </div>
    </div>
  );
}

export function DrawingSparkline({ inView }: { inView: boolean }): JSX.Element {
  const values = [62, 71, 68, 74, 78, 82, 85, 88, 92, 95, 98];
  const w = 240, h = 44;
  const min = Math.min(...values), max = Math.max(...values), r = max - min || 1;
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => [i * step, h - ((v - min) / r) * h] as const);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
      <path d={area} fill="rgba(229,180,84,0.14)" />
      <motion.path
        d={path} fill="none" stroke="var(--md-amber)" strokeWidth="1.6" strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }} animate={inView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ duration: 1.6, ease: [0.2, 0.7, 0.3, 1], delay: 0.5 }}
      />
      <motion.circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill="var(--md-amber)"
        initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 2, duration: 0.3 }}
      />
    </svg>
  );
}
/* ══════════════════════════════════════════════════════════════════
 * PATCH 16 · Below-the-fold story sections
 *
 * The visitor experiences ONE continuous conversation:
 *   Why Orkexra Exists
 *   → First, We Learn Your Business
 *   → Tell Us the Goal
 *   → Review the Strategy
 *   → Meet the Department
 *   → Review Only What Matters
 *   → Publishing
 *   → Autopilot
 *   → Your Morning
 *   → Built for Growing Brands
 *   → Final CTA
 *
 * No technical jargon. No fabricated metrics. No provider names.
 * ══════════════════════════════════════════════════════════════════ */

/* ── Section: Why Orkexra Exists ─────────────────────────── */
function WhyExists(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  return (
    <section
      id="why"
      data-testid="landing-why-exists"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto grid max-w-[1180px] gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
        >
          <div className="md-eyebrow md-eyebrow--amber">Why Orkexra exists</div>
          <h2 className="mt-4 font-display italic tracking-tight text-[var(--md-ivory-0)] text-4xl md:text-6xl">
            Business owners don&rsquo;t need more tools.<br />
            <span className="brand-gradient-text">They need more time.</span>
          </h2>
          <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--md-fog)]">
            Marketing has quietly become another full-time job. Another calendar to
            manage. Another inbox to answer. Another set of decisions to make when
            you already had a business to run.
          </p>
          <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-[var(--md-fog)]">
            Orkexra gives you a marketing department. You tell it what you&rsquo;re
            trying to accomplish. It handles the rest — the strategy, the writing,
            the design, the editing, the schedule — and brings you only what matters.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/70 p-6 backdrop-blur"
        >
          <div className="md-eyebrow">What most owners live with</div>
          <ul className="mt-4 space-y-3 text-[14px]">
            {[
              "Five tools, five subscriptions, five logins.",
              "Ideas that never make it out of the notes app.",
              "A calendar that starts strong and falls silent.",
              "Vendors and freelancers who never quite fit.",
            ].map((s) => (
              <li key={s} className="flex items-start gap-3 text-[var(--md-ivory-0)]">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--md-fog)]" /> {s}
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-[color:var(--md-line)] pt-5">
            <div className="md-eyebrow md-eyebrow--amber">What Orkexra is</div>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--md-ivory-0)]">
              One marketing department. One place to approve the work. One story
              told consistently, everywhere.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Section: First, We Learn Your Business (Brand Brain) ────── */
function LearnYourBusiness(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const facets = [
    { icon: <MessageSquare className="h-4 w-4" />, label: "Voice",     line: "How you sound when you sound like yourself." },
    { icon: <User className="h-4 w-4" />,           label: "Audience",  line: "Who you're for — and who you're not." },
    { icon: <Layers className="h-4 w-4" />,         label: "Offers",    line: "What you sell, and how it's positioned." },
    { icon: <ShieldCheck className="h-4 w-4" />,    label: "Standards", line: "What's on-brand. What's off-limits." },
    { icon: <Target className="h-4 w-4" />,         label: "Goals",     line: "What you're trying to accomplish this season." },
    { icon: <FileText className="h-4 w-4" />,       label: "Assets",    line: "The work you've already made, remembered." },
  ];
  return (
    <section
      id="learn"
      data-testid="landing-learn-your-business"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">First, we learn your business</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Before we make anything,<br />
            <span className="brand-gradient-text">we listen.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            Your business isn&rsquo;t a template. The department gets to know you the
            same way a great hire would — patiently, thoughtfully, and only asking
            what actually matters.
          </p>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {facets.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.06 }}
              className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5"
            >
              <div className="flex items-center gap-3">
                <span aria-hidden className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
                  {f.icon}
                </span>
                <b className="text-[15px] text-[var(--md-ivory-0)]">{f.label}</b>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-[var(--md-fog)]">{f.line}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Tell Us the Goal ───────────────────────────────── */
function TellUsTheGoal(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  return (
    <section
      id="goal"
      data-testid="landing-tell-us-goal"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1080px] text-center">
        <div className="md-eyebrow md-eyebrow--amber">Tell us the goal</div>
        <motion.h2
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
          className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
        >
          Orkexra begins with a different question.
        </motion.h2>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.15 }}
            className="rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-6 text-left"
          >
            <div className="md-eyebrow md-eyebrow--cyan">Most tools ask</div>
            <p className="mt-3 font-display italic text-2xl leading-snug text-[var(--md-fog)]">
              &ldquo;What would you like to create?&rdquo;
            </p>
            <p className="mt-4 text-[13px] leading-relaxed text-[var(--md-fog)]">
              You get a blank canvas, a blinking cursor, and a to-do list that
              belongs to someone else.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }}
            className="rounded-2xl border p-6 text-left"
            style={{
              borderColor: "rgba(229,180,84,0.4)",
              background: "linear-gradient(180deg, rgba(229,180,84,0.10), rgba(229,180,84,0.03))",
              boxShadow: "inset 0 0 40px rgba(229,180,84,0.06)",
            }}
          >
            <div className="md-eyebrow md-eyebrow--amber">Orkexra asks</div>
            <p className="mt-3 font-display italic text-2xl leading-snug text-[var(--md-ivory-0)]">
              &ldquo;What are you trying to accomplish?&rdquo;
            </p>
            <p className="mt-4 text-[13px] leading-relaxed text-[var(--md-ivory-0)]">
              The department starts where a great collaborator starts — with your
              goal. Everything else follows from there.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Section: The Flow (visual centerpiece) ──────────────────── */
function CampaignFlow(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const steps = [
    { n: "01", label: "Business goal",     line: "You tell us what you want to accomplish." },
    { n: "02", label: "Marketing strategy", line: "The department prepares the plan." },
    { n: "03", label: "Content creation",   line: "Every asset the campaign needs." },
    { n: "04", label: "Approval",           line: "You review. Nothing ships without you." },
    { n: "05", label: "Publishing",         line: "The plan goes live on cadence." },
    { n: "06", label: "Autopilot",          line: "The next campaign is already forming." },
  ];
  const contentKinds = [
    { icon: <Film className="h-3.5 w-3.5" />,       label: "Video" },
    { icon: <ImageIcon className="h-3.5 w-3.5" />,  label: "Images" },
    { icon: <Rss className="h-3.5 w-3.5" />,        label: "Social posts" },
    { icon: <Mail className="h-3.5 w-3.5" />,       label: "Email" },
    { icon: <BookOpen className="h-3.5 w-3.5" />,   label: "Blog" },
    { icon: <Globe className="h-3.5 w-3.5" />,      label: "Landing pages" },
    { icon: <Megaphone className="h-3.5 w-3.5" />,  label: "Ads" },
    { icon: <Star className="h-3.5 w-3.5" />,       label: "Google Business" },
  ];
  return (
    <section
      id="flow"
      data-testid="landing-campaign-flow"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">How a campaign moves</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Goal in.<br />
            <span className="brand-gradient-text">Marketing out.</span>
          </motion.h2>
        </div>

        {/* Rail of six steps with expanded Content Creation card */}
        <div className="mt-16 grid gap-3 md:grid-cols-2 lg:grid-cols-6">
          {steps.map((s, i) => {
            const highlighted = s.n === "03";
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 10 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                className={`relative rounded-xl border p-4 ${
                  highlighted
                    ? "border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/5 shadow-[0_10px_40px_-15px_rgba(229,180,84,0.25)]"
                    : "border-[color:var(--md-line)] bg-[var(--md-ink-1)]"
                }`}
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--md-amber)]">{s.n}</div>
                <div className="mt-2 text-[14px] font-semibold text-[var(--md-ivory-0)]">{s.label}</div>
                <p className="mt-2 text-[11px] leading-relaxed text-[var(--md-fog)]">{s.line}</p>
                {highlighted && (
                  <div className="mt-3 border-t border-[color:var(--md-amber)]/25 pt-3">
                    <div className="grid grid-cols-2 gap-1.5">
                      {contentKinds.map((k) => (
                        <span key={k.label} className="flex items-center gap-1.5 rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-1.5 py-1 text-[10px] text-[var(--md-ivory-0)]">
                          <span className="text-[var(--md-amber)]">{k.icon}</span>{k.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Connector */}
                {i < steps.length - 1 && (
                  <div aria-hidden className="pointer-events-none absolute -right-1.5 top-6 hidden text-[var(--md-amber)]/50 lg:block">
                    <ChevronRight className="h-4 w-4" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Everything we create (Video restored as a pillar) ─ */
function EverythingWeCreate(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const videoTypes = [
    { label: "Reels",             icon: <Film className="h-4 w-4" /> },
    { label: "TikTok",            icon: <Film className="h-4 w-4" /> },
    { label: "YouTube Shorts",    icon: <Youtube className="h-4 w-4" /> },
    { label: "Long-form YouTube", icon: <Youtube className="h-4 w-4" /> },
    { label: "AI-Powered Presenter Videos", icon: <Video className="h-4 w-4" /> },
    { label: "Motion Graphics",   icon: <Sparkles className="h-4 w-4" /> },
    { label: "Video Ads",         icon: <Megaphone className="h-4 w-4" /> },
  ];
  const restTypes = [
    { label: "Images",        icon: <ImageIcon className="h-4 w-4" />,   family: "Creative" },
    { label: "Blogs",         icon: <BookOpen className="h-4 w-4" />,    family: "Long-form" },
    { label: "Emails",        icon: <Mail className="h-4 w-4" />,        family: "Email" },
    { label: "Landing pages", icon: <Globe className="h-4 w-4" />,       family: "Web" },
    { label: "Flyers",        icon: <FileText className="h-4 w-4" />,    family: "Print" },
    { label: "Ads",           icon: <Megaphone className="h-4 w-4" />,   family: "Paid" },
    { label: "Google Business", icon: <Star className="h-4 w-4" />,      family: "Local" },
    { label: "Social posts",  icon: <Rss className="h-4 w-4" />,         family: "Social" },
  ];
  return (
    <section
      id="everything"
      data-testid="landing-everything-we-create"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Everything the department creates</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            One team.<br />
            <span className="brand-gradient-text">Every format.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            From a reel to a landing page — the department handles the full range.
            No hand-offs. No agencies. Everything ships in your voice.
          </p>
        </div>

        {/* Video pillar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 overflow-hidden rounded-2xl border border-[color:var(--md-amber)]/30 p-6 md:p-8"
          style={{
            background: "linear-gradient(180deg, rgba(229,180,84,0.06), rgba(229,180,84,0.02))",
            boxShadow: "inset 0 0 60px rgba(229,180,84,0.05)",
          }}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="md-eyebrow md-eyebrow--amber">Video · a first-class pillar</div>
              <h3 className="mt-2 font-display italic text-3xl md:text-4xl text-[var(--md-ivory-0)]">
                From reels to full-length.
              </h3>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-[var(--md-fog)]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--md-amber)]" />
              Three renders in progress
            </div>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {videoTypes.map((v, i) => (
              <motion.div
                key={v.label}
                initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.35 + i * 0.05 }}
                className="group flex items-center gap-3 rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-3 transition hover:border-[color:var(--md-amber)]/40"
              >
                <span
                  aria-hidden
                  className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)] transition-transform duration-300 group-hover:scale-105"
                >
                  {v.icon}
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-[var(--md-ivory-0)]">{v.label}</div>
                  <div className="md-eyebrow">Rendered in your voice</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Everything else grid */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {restTypes.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.35, delay: 0.55 + i * 0.05 }}
              className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4"
            >
              <div className="flex items-center gap-3">
                <span aria-hidden className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] text-[var(--md-ivory-0)]">
                  {t.icon}
                </span>
                <div>
                  <div className="md-eyebrow">{t.family}</div>
                  <div className="text-[14px] font-semibold text-[var(--md-ivory-0)]">{t.label}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Show outputs — work leaving the department ─────── */
function OutputShowcase(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const outputs: Array<{ channel: string; kind: string; title: string; body: string; accent: string; status: "Published" | "Scheduled" | "Ready" | "Awaiting approval" }> = [
    { channel: "Instagram",      kind: "Reel",       title: "Behind the counter · 15s",     body: "A quick look at how the beans arrive each Monday.", accent: "#F0B98A", status: "Published"        },
    { channel: "Facebook",       kind: "Post",       title: "Community weekend feature",     body: "Meet the neighbourhood shop keeping our lights warm.", accent: "#7EE7A3", status: "Scheduled"       },
    { channel: "TikTok",         kind: "Short",      title: "Latte pour · 15s",              body: "Slow motion. Warm light. On brand.", accent: "#F5C15C", status: "Ready"                  },
    { channel: "YouTube",        kind: "Short",      title: "Meet the roaster · teaser",     body: "60-second story cut for discovery.", accent: "#8FCEF5", status: "Scheduled"                },
    { channel: "Email",          kind: "Newsletter", title: "Weekly customer update",        body: "A round-up your subscribers actually open.", accent: "#F5C15C", status: "Ready"              },
    { channel: "Blog",           kind: "Article",    title: "The story behind our roast",    body: "A long-form piece anchored to your origin pillar.", accent: "#7EE7A3", status: "Awaiting approval" },
    { channel: "Landing page",   kind: "Campaign",   title: "Subscribe & save · Autumn",      body: "A conversion-focused page for the seasonal drop.", accent: "#F5C15C", status: "Ready"           },
    { channel: "Google Business", kind: "Update",    title: "Autumn hours & new photos",     body: "Fresh imagery and updated hours for local search.", accent: "#8FCEF5", status: "Published"       },
    { channel: "Flyer",          kind: "Print",      title: "Community board · A4",           body: "A print-ready flyer for the shop noticeboard.", accent: "#F0B98A", status: "Ready"             },
    { channel: "Pinterest",      kind: "Pin",        title: "Autumn recipe · pour-over",     body: "A vertical pin sized for discovery feeds.", accent: "#7EE7A3", status: "Scheduled"           },
    { channel: "LinkedIn",       kind: "Post",       title: "Founder's note · this week",    body: "A considered post for a professional audience.", accent: "#8FCEF5", status: "Ready"             },
  ];
  const statusMeta: Record<typeof outputs[number]["status"], { color: string; bg: string; ring: string }> = {
    "Published":         { color: "#7EE7A3", bg: "rgba(74,222,128,0.10)",  ring: "rgba(74,222,128,0.35)" },
    "Scheduled":         { color: "#8FCEF5", bg: "rgba(125,211,252,0.10)", ring: "rgba(125,211,252,0.35)" },
    "Ready":             { color: "#F5C15C", bg: "rgba(229,180,84,0.10)",  ring: "rgba(229,180,84,0.35)" },
    "Awaiting approval": { color: "#F0B98A", bg: "rgba(240,185,138,0.10)", ring: "rgba(240,185,138,0.40)" },
  };
  return (
    <section
      id="outputs"
      data-testid="landing-outputs"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Work leaving the department</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            The work<br />
            <span className="brand-gradient-text">speaks for itself.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            A glance at what the department produces in a normal week — every
            format, every channel, every asset built to your Brand Brain.
          </p>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {outputs.map((o, i) => {
            const m = statusMeta[o.status];
            return (
              <motion.div
                key={`${o.channel}-${o.title}`}
                initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.04 }}
                className="group relative overflow-hidden rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] transition hover:border-[color:var(--md-amber)]/40"
              >
                {/* Accent stripe */}
                <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: o.accent }} />
                <div className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest" style={{ background: `${o.accent}18`, color: o.accent }}>
                        {o.channel}
                      </span>
                      <span className="md-eyebrow">{o.kind}</span>
                    </div>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                      style={{ background: m.bg, color: m.color, boxShadow: `inset 0 0 0 1px ${m.ring}` }}
                    >
                      {o.status}
                    </span>
                  </div>
                  <div className="mt-3 text-[15px] font-semibold text-[var(--md-ivory-0)]">{o.title}</div>
                  <p className="mt-2 text-[13px] leading-relaxed text-[var(--md-fog)]">{o.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Powered by Modern AI (Patch 18) ────────────────── */
function TrustSection(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();

  // Only names that are genuinely wired into the current codebase.
  // Backend integrations verified in /app/backend/app/integrations/ai/*
  // and /app/backend/app/integrations/ai/creative/*.
  const categories: Array<{
    label: string;
    icon: JSX.Element;
    line: string;
    techs: string[];
  }> = [
    {
      label: "Strategy & writing",
      icon: <PenLine className="h-4 w-4" />,
      line: "Used for campaign thinking, long-form writing, structured content, and specialized language tasks.",
      techs: ["OpenAI", "Anthropic Claude", "Google AI"],
    },
    {
      label: "Images & creative",
      icon: <ImageIcon className="h-4 w-4" />,
      line: "Used for campaign artwork, social graphics, visual concepts, and branded creative assets.",
      techs: ["OpenAI Images", "Replicate", "FLUX"],
    },
    {
      label: "Video",
      icon: <Film className="h-4 w-4" />,
      line: "Used for short-form video, presenter content, campaign visuals, motion assets, and video advertising.",
      techs: ["Runway", "HeyGen"],
    },
    {
      label: "Voice",
      icon: <Video className="h-4 w-4" />,
      line: "Used for natural voiceovers, narration, campaign audio, and presenter content.",
      techs: ["ElevenLabs"],
    },
  ];

  return (
    <section
      id="powered-by"
      data-testid="landing-trust-section"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Modern technology. One coordinated department.</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Powered by Modern AI.<br />
            <span className="brand-gradient-text">Directed by You.</span>
          </motion.h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-relaxed text-[var(--md-fog)]">
            Your Marketing Department coordinates leading AI technologies behind the
            scenes for strategy, writing, image generation, video production, voice,
            and automation. You never have to learn, manage, or switch between them.
            Orkexra selects the right capability for each task and delivers the
            finished work through one coordinated experience.
          </p>
        </div>

        {/* Capability categories — restrained monochrome */}
        <div className="mt-16 grid gap-3 md:grid-cols-2">
          {categories.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 10 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              className="group rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5 transition hover:border-[color:var(--md-fog)]/40"
            >
              <div className="flex items-center gap-3">
                <span aria-hidden className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] text-[var(--md-fog)]">
                  {c.icon}
                </span>
                <div className="md-eyebrow">{c.label}</div>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-[var(--md-fog)]">{c.line}</p>
              {/* Subtle divider */}
              <div aria-hidden className="mt-4 h-px bg-[color:var(--md-line)]" />
              {/* Monochrome wordmark chips — no colour, no logos, no trademarks */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {c.techs.map((t) => (
                  <span
                    key={t}
                    className="rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-2.5 py-1 text-[11px] font-medium text-[var(--md-ivory-0)]/85 transition group-hover:text-[var(--md-ivory-0)]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Core trust message */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.55 }}
          className="mt-14 rounded-2xl border border-[color:var(--md-amber)]/20 bg-[var(--md-ink-1)]/60 p-6 md:p-8"
        >
          <div className="md-eyebrow md-eyebrow--amber">World-class technology. One Marketing Department.</div>
          <h3 className="mt-3 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
            Technology changes quickly. Your workflow should not.
          </h3>
          <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-[var(--md-fog)]">
            Orkexra is built to support multiple providers, allowing the
            platform to evolve without forcing you to change how you work. The AI
            provides capability. Orkexra provides coordination. You provide
            direction.
          </p>
        </motion.div>

        {/* Legal disclosure — present without dominating */}
        <p
          data-testid="landing-trust-disclosure"
          className="mx-auto mt-8 max-w-3xl text-center text-[11px] leading-relaxed text-[var(--md-fog)]/75"
        >
          Orkexra integrates with third-party AI and publishing technologies to
          deliver its services. Provider availability may change as the platform
          evolves. References to third-party names do not imply sponsorship,
          endorsement, or partnership.
        </p>
      </div>
    </section>
  );
}

/* ── Section: Publishing Destinations (Patch 18) ─────────────── */
function PublishingDestinations(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();

  // Status reflects the current codebase:
  //   • Domain layer supports all Channel enum values via SimulatedProvider,
  //     but no live OAuth connector ships yet → "In integration".
  //   • Google Business Profile is not yet in the Channel enum → "Coming soon".
  type Status = "Available" | "In integration" | "Coming soon";
  const destinations: Array<{ name: string; status: Status }> = [
    { name: "Instagram",              status: "In integration" },
    { name: "Facebook",               status: "In integration" },
    { name: "TikTok",                 status: "In integration" },
    { name: "YouTube",                status: "In integration" },
    { name: "LinkedIn",               status: "In integration" },
    { name: "Pinterest",              status: "In integration" },
    { name: "X",                      status: "In integration" },
    { name: "Google Business Profile", status: "Coming soon" },
  ];
  const statusMeta: Record<Status, { color: string; bg: string; ring: string }> = {
    "Available":       { color: "#7EE7A3", bg: "rgba(74,222,128,0.10)",  ring: "rgba(74,222,128,0.35)" },
    "In integration":  { color: "#F5C15C", bg: "rgba(229,180,84,0.10)",  ring: "rgba(229,180,84,0.35)" },
    "Coming soon":     { color: "#8FCEF5", bg: "rgba(125,211,252,0.10)", ring: "rgba(125,211,252,0.35)" },
  };
  return (
    <section
      id="publishing-destinations"
      data-testid="landing-publishing-destinations"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Where your work goes</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Connect the platforms<br />
            <span className="brand-gradient-text">where your audience already follows you.</span>
          </motion.h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-relaxed text-[var(--md-fog)]">
            Your accounts remain yours. Once connected, your Publishing Team can
            prepare, schedule, and publish approved content according to your
            selected trust level.
          </p>
        </div>

        <div className="mt-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {destinations.map((d, i) => {
            const m = statusMeta[d.status];
            return (
              <motion.div
                key={d.name}
                initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.15 + i * 0.05 }}
                className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4 transition hover:border-[color:var(--md-fog)]/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[14px] font-semibold text-[var(--md-ivory-0)]">{d.name}</span>
                  <span
                    className="whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ background: m.bg, color: m.color, boxShadow: `inset 0 0 0 1px ${m.ring}` }}
                  >
                    {d.status}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="mx-auto mt-10 max-w-3xl text-center text-[11px] leading-relaxed text-[var(--md-fog)]/75">
          Availability may change as platforms release new APIs and integration
          partners. You&rsquo;ll always see a platform&rsquo;s current status before
          you connect an account.
        </p>
      </div>
    </section>
  );
}

/* ── Section: Review the Strategy ────────────────────────────── */
export function ReviewTheStrategy(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const steps = [
    { n: "01", t: "You share the goal",    l: "In your words. A sentence is enough." },
    { n: "02", t: "The strategy is prepared", l: "Positioning, audience, channels, cadence — laid out in plain language." },
    { n: "03", t: "You review and approve", l: "Nothing begins production until you say yes." },
    { n: "04", t: "The department goes to work", l: "Only after approval — never before." },
  ];
  return (
    <section
      id="strategy"
      data-testid="landing-review-strategy"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Review the strategy</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Strategy first.<br />
            <span className="brand-gradient-text">Always.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            No campaign begins until you&rsquo;ve seen the plan and approved it. This is
            how professional marketing teams work. It&rsquo;s how Orkexra works.
          </p>
        </div>

        <ol className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <motion.li
              key={s.n}
              initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5"
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--md-amber)]">{s.n}</div>
              <div className="mt-3 text-[15px] font-semibold text-[var(--md-ivory-0)]">{s.t}</div>
              <p className="mt-2 text-[13px] leading-relaxed text-[var(--md-fog)]">{s.l}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ── Section: Meet the Department ────────────────────────────── */
function MeetTheDepartment(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const roles: Array<{ icon: JSX.Element; role: string; line: string; kpi: string; kpiLabel: string; accent: string; }> = [
    { icon: <Compass className="h-4 w-4" />,     role: "Strategy",    line: "Plans your campaigns — positioning, audience, cadence.",         kpi: "1",  kpiLabel: "campaign in planning",  accent: "#F5C15C" },
    { icon: <PenLine className="h-4 w-4" />,     role: "Copywriting", line: "Writes blogs, captions, emails, and ads in your voice.",         kpi: "6",  kpiLabel: "drafts in flight",        accent: "#F5C15C" },
    { icon: <Palette className="h-4 w-4" />,     role: "Creative",    line: "Designs graphics and imagery for every channel.",                kpi: "4",  kpiLabel: "assets ready",            accent: "#7EE7A3" },
    { icon: <Film className="h-4 w-4" />,        role: "Video",       line: "Creates reels, shorts, and full-length videos end-to-end.",       kpi: "3",  kpiLabel: "renders in progress",     accent: "#F0B98A" },
    { icon: <Calendar className="h-4 w-4" />,    role: "Publishing",  line: "Schedules and distributes everything, everywhere, on cadence.",   kpi: "5",  kpiLabel: "posts queued this week",  accent: "#8FCEF5" },
    { icon: <ShieldCheck className="h-4 w-4" />, role: "Autopilot",   line: "Keeps the department moving. You decide how much authority.",     kpi: "Assistant", kpiLabel: "trust level", accent: "#F5C15C" },
  ];
  return (
    <section
      id="department"
      data-testid="landing-meet-department"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Meet the department</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Six roles.<br />
            <span className="brand-gradient-text">One team.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            The people you never had to hire. They talk to each other. They
            remember what worked. They only escalate to you when it matters.
          </p>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {roles.map((r, i) => (
            <motion.div
              key={r.role}
              initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
              className="group relative overflow-hidden rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5 transition-shadow hover:shadow-[0_20px_60px_-20px_rgba(229,180,84,0.20)]"
            >
              {/* Soft glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                style={{ background: r.accent }}
              />
              <div className="relative flex items-center gap-3">
                <span
                  aria-hidden
                  className="grid h-10 w-10 place-items-center rounded-lg border transition-transform duration-300 group-hover:scale-105"
                  style={{
                    borderColor: `${r.accent}55`,
                    background: `${r.accent}15`,
                    color: r.accent,
                  }}
                >
                  {r.icon}
                </span>
                <b className="text-[16px] text-[var(--md-ivory-0)]">{r.role}</b>
              </div>
              <p className="relative mt-3 text-[13px] leading-relaxed text-[var(--md-fog)]">{r.line}</p>
              {/* Live KPI strip */}
              <div className="relative mt-5 flex items-center justify-between border-t border-[color:var(--md-line)] pt-3">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: r.accent }} />
                  <span className="md-eyebrow">{r.kpiLabel}</span>
                </div>
                <span className="font-mono text-[13px] font-semibold" style={{ color: r.accent }}>{r.kpi}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Review Only What Matters ───────────────────────── */
function ReviewOnlyWhatMatters(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const actions = [
    { icon: <CheckCircle2 className="h-4 w-4" />, label: "Approve",  line: "Say yes. It moves to the calendar." },
    { icon: <RotateCcw className="h-4 w-4" />,    label: "Return",   line: "Ask for a revision. The team tries again." },
    { icon: <MessageSquare className="h-4 w-4" />,label: "Comment",  line: "Leave a note. Context stays with the work." },
    { icon: <Archive className="h-4 w-4" />,      label: "Archive",  line: "Set it aside. Nothing is ever lost." },
  ];
  return (
    <section
      id="review"
      data-testid="landing-review-what-matters"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto grid max-w-[1180px] gap-14 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1fr)] lg:items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
        >
          <div className="md-eyebrow md-eyebrow--amber">Review only what matters</div>
          <h2 className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl">
            One place.<br />
            <span className="brand-gradient-text">Everything ready.</span>
          </h2>
          <p className="mt-6 max-w-lg text-[16px] leading-relaxed text-[var(--md-fog)]">
            No inbox. No Slack. No pinging. Everything the department has prepared
            arrives in one calm queue. You choose what happens next.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/70 p-6 backdrop-blur"
        >
          <div className="md-eyebrow">Content review</div>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {actions.map((a) => (
              <li key={a.label} className="flex items-start gap-3 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] p-4">
                <span aria-hidden className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-md border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
                  {a.icon}
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-[var(--md-ivory-0)]">{a.label}</div>
                  <div className="mt-1 text-[12px] leading-relaxed text-[var(--md-fog)]">{a.line}</div>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Section: Publishing ─────────────────────────────────────── */
function PublishingSection(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  return (
    <section
      id="publishing"
      data-testid="landing-publishing"
      className="border-y border-[color:var(--md-line)] px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Publishing</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            The plan is ready<br />
            <span className="brand-gradient-text">before the week begins.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            Orkexra prepares the publishing plan the way a great marketing team
            would — with intent, with cadence, with a reason for every post.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-14 rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/70 p-6 backdrop-blur"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <b className="text-[14px] text-[var(--md-ivory-0)]">This week</b>
            <span className="md-eyebrow">Prepared for your approval</span>
          </div>
          <div className="mt-4 grid grid-cols-7 gap-2">
            {[
              { d: "Mon", ch: "IG",    t: "Story of the week" },
              { d: "Tue", ch: "LI",    t: "Founder's note" },
              { d: "Wed", ch: "",      t: "" },
              { d: "Thu", ch: "EMAIL", t: "Monthly update" },
              { d: "Fri", ch: "IG",    t: "Customer spotlight" },
              { d: "Sat", ch: "",      t: "" },
              { d: "Sun", ch: "TT",    t: "Behind the scenes" },
            ].map((c) => (
              <div key={c.d} className="min-h-[110px] rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] p-2">
                <div className="md-eyebrow">{c.d}</div>
                {c.t ? (
                  <div className="mt-2 rounded-md border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10 p-2">
                    <div className="md-eyebrow md-eyebrow--amber">{c.ch}</div>
                    <div className="mt-1 text-[11px] leading-snug text-[var(--md-ivory-0)]">{c.t}</div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </motion.div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-[12px] leading-relaxed text-[var(--md-fog)]">
          Publishing goes live once you connect a supported platform. Until then,
          Orkexra keeps the plan ready — nothing goes out without your say-so.
        </p>
      </div>
    </section>
  );
}

/* ── Section: Autopilot ──────────────────────────────────────── */
function AutopilotSection(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const levels = [
    { name: "Advisor",   line: "The department suggests. You decide." },
    { name: "Assistant", line: "The department prepares. You approve." },
    { name: "Operator",  line: "The department runs the plan. You review." },
    { name: "Executive", line: "The department leads. You&rsquo;re only pinged when it matters." },
  ];
  return (
    <section
      id="autopilot"
      data-testid="landing-autopilot"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Autopilot</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
          >
            Four levels of trust.<br />
            <span className="brand-gradient-text">You&rsquo;re always in charge.</span>
          </motion.h2>
          <p className="mt-6 text-[16px] leading-relaxed text-[var(--md-fog)]">
            Start where you&rsquo;re comfortable. Move up when you&rsquo;re ready. Move back
            any time.
          </p>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {levels.map((lv, i) => (
            <motion.div
              key={lv.name}
              initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5"
            >
              <div className="md-eyebrow md-eyebrow--amber">{lv.name}</div>
              <p
                className="mt-3 text-[14px] leading-relaxed text-[var(--md-ivory-0)]"
                dangerouslySetInnerHTML={{ __html: lv.line }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Your Morning ───────────────────────────────────── */
function YourMorning(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const yesterday = [
    "Published three videos",
    "Scheduled seven social posts",
    "Finished two blogs",
    "Prepared one newsletter",
    "Updated Google Business",
  ];
  return (
    <section
      id="morning"
      data-testid="landing-your-morning"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto grid max-w-[1180px] gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] lg:items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
        >
          <div className="md-eyebrow md-eyebrow--amber">Your morning</div>
          <h2 className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl">
            One short brief.<br />
            <span className="brand-gradient-text">The rest of the day is yours.</span>
          </h2>
          <p className="mt-6 max-w-lg text-[16px] leading-relaxed text-[var(--md-fog)]">
            Every morning Orkexra tells you what shipped yesterday, what needs
            your attention, and what can wait. No metrics wall. No red dots. Just
            what an executive would want to know.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="overflow-hidden rounded-2xl border border-[color:var(--md-amber)]/25 bg-[var(--md-ink-1)]/70 backdrop-blur"
          style={{ boxShadow: "0 40px 100px -40px rgba(0,0,0,0.6)" }}
        >
          {/* Card chrome */}
          <div className="flex items-center gap-2 border-b border-[color:var(--md-line)] px-5 py-3">
            <span aria-hidden className="grid h-7 w-7 place-items-center rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
              <Coffee className="h-3.5 w-3.5" />
            </span>
            <b className="text-[13px] text-[var(--md-ivory-0)]">Morning brief · Tuesday</b>
            <span className="ml-auto md-eyebrow md-eyebrow--amber md-eyebrow--live">Live</span>
          </div>

          {/* Card body */}
          <div className="p-6">
            <div className="font-display italic text-2xl text-[var(--md-ivory-0)]">Good morning.</div>
            <div className="mt-1 text-[13px] text-[var(--md-fog)]">Yesterday your Marketing Department&hellip;</div>

            <ul className="mt-5 space-y-2.5 text-[14px]">
              {yesterday.map((line, i) => (
                <motion.li
                  key={line}
                  initial={{ opacity: 0, x: -6 }} animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.35, delay: 0.35 + i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--md-good)]" />
                  <span className="text-[var(--md-ivory-0)]">{line}</span>
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, x: -6 }} animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.75 }}
                className="flex items-start gap-3"
              >
                <span className="mt-0.5 grid h-4 w-4 flex-shrink-0 place-items-center rounded-full border border-[color:var(--md-amber)]/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--md-amber)]" />
                </span>
                <span className="text-[var(--md-ivory-0)]">One campaign awaiting your approval</span>
              </motion.li>
            </ul>

            <div className="mt-6 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/60 p-3">
              <div className="md-eyebrow">Today</div>
              <div className="mt-1 text-[14px] text-[var(--md-ivory-0)]">No action required.</div>
            </div>
          </div>

          {/* Card footer */}
          <div className="border-t border-[color:var(--md-line)] px-5 py-3 md-eyebrow">
            Operational only. No fabricated metrics.
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Section: Built for Growing Brands ───────────────────────── */
function BuiltForGrowingBrands(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  const kinds = [
    "Local businesses",
    "Creators",
    "Musicians",
    "Authors",
    "Coaches",
    "Consultants",
    "Ecommerce",
    "Nonprofits",
    "Agencies",
  ];
  return (
    <section
      id="who"
      data-testid="landing-built-for"
      className="px-6 py-24 md:py-32"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px] text-center">
        <div className="md-eyebrow md-eyebrow--amber">Built for growing brands</div>
        <motion.h2
          initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
          className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl"
        >
          Wherever consistent marketing matters.
        </motion.h2>
        <p className="mx-auto mt-6 max-w-2xl text-[16px] leading-relaxed text-[var(--md-fog)]">
          Orkexra is designed for the people who care about how their brand
          shows up — even when the day gets long.
        </p>

        <div className="mt-14 flex flex-wrap justify-center gap-3">
          {kinds.map((k, i) => (
            <motion.span
              key={k}
              initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.35, delay: 0.15 + i * 0.05 }}
              className="rounded-full border border-[color:var(--md-line)] bg-[var(--md-ink-1)] px-4 py-1.5 text-[13px] text-[var(--md-ivory-0)]"
            >
              {k}
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Section: Final CTA ──────────────────────────────────────── */
function FinalCTA(): JSX.Element {
  return (
    <section
      id="cta"
      data-testid="landing-final-cta"
      className="relative overflow-hidden px-6 py-28"
      style={{ scrollMarginTop: 80 }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 500px at 30% 50%, rgba(229,180,84,0.18), transparent 60%), radial-gradient(500px 300px at 80% 50%, rgba(74,222,128,0.10), transparent 60%)",
        }}
      />
      <div className="mx-auto max-w-[1080px] text-center">
        <motion.h2
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="font-display italic tracking-tight text-4xl md:text-6xl"
        >
          Tell us the goal.<br />
          <span className="brand-gradient-text">We&rsquo;ll handle the marketing.</span>
        </motion.h2>
        <p className="mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--md-fog)]">
          One conversation. One plan. One department. Ready when you are.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/onboarding"
            data-testid="final-cta-primary"
            className="md-btn md-btn--primary"
            style={{ padding: "14px 24px", fontSize: 14 }}
          >
            Build Your First Campaign <ChevronRight className="h-3.5 w-3.5" />
          </Link>
          <a
            href="#pricing"
            data-testid="final-cta-secondary-anchor"
            className="hidden"
          >
            {/* fallback for legacy in-page anchor */}
          </a>
          <Link
            to="/pricing"
            data-testid="final-cta-secondary"
            className="md-btn md-btn--ghost"
            style={{ padding: "14px 22px", fontSize: 14 }}
          >
            See Pricing
          </Link>
        </div>
        <div className="mt-8 md-eyebrow">
          No credit card to start. No lock-in. No jargon.
        </div>
      </div>
    </section>
  );
}

/* ── Footer ─────────────────────────────────────────────────── */
function PublicFooter(): JSX.Element {
  return (
    <footer className="border-t border-[color:var(--md-line)] px-6 py-10">
      <div className="mx-auto max-w-[1280px] space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-book-executive"
            className="md-btn md-btn--ghost whitespace-nowrap"
          >
            <Calendar className="h-3.5 w-3.5" /> Book a walkthrough
          </a>
          <Link
            to="/login"
            data-testid="footer-signin"
            className="md-btn md-btn--ghost sm:hidden whitespace-nowrap"
          >
            Sign in
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 md-eyebrow">
          <span>© 2026 Orkexra · Your Marketing Department.</span>
          <span>SOC 2 · GDPR · Stripe Managed Payments</span>
        </div>
      </div>
    </footer>
  );
}


/* ── Section: Choose how to explore Orkexra (Patch 34.1.10) ──── */
function ChooseHowToExplore(): JSX.Element {
  const { ref, inView } = useAnimatedInView<HTMLDivElement>();
  return (
    <section
      id="explore"
      data-testid="landing-choose-how-to-explore"
      className="border-y border-[color:var(--md-line)] px-6 py-20 md:py-28"
      style={{ scrollMarginTop: 80 }}
    >
      <div ref={ref} className="mx-auto max-w-[1180px]">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Choose how to explore Orkexra</div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="mt-4 font-display italic tracking-tight text-4xl md:text-5xl text-[var(--md-ivory-0)]"
          >
            See the finished system,<br />
            <span className="brand-gradient-text">or watch it learn a business.</span>
          </motion.h2>
          <p className="mt-6 text-[15px] leading-relaxed text-[var(--md-fog)]">
            No credentials. No AI is triggered on your behalf. Both paths keep
            provider names, costs, and internal metadata out of sight — you're
            seeing the finished product surface, not the plumbing.
          </p>
        </div>

        <div className="mx-auto mt-12 grid gap-6 lg:grid-cols-2">
          {/* Ready-to-Work — primary */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="rounded-2xl border border-[color:var(--md-amber)]/40 bg-gradient-to-b from-[var(--md-amber)]/10 to-transparent p-6 md:p-8"
          >
            <div className="md-eyebrow md-eyebrow--amber">See the finished system</div>
            <h3 className="mt-3 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
              Explore a fully configured workspace.
            </h3>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--md-fog)]">
              The Business Brain, campaigns, approvals, content, calendar, and
              publishing are already working together. Step inside and see what
              a live Orkexra department looks like at the end of onboarding.
            </p>
            <Link
              to="/demo/workspace"
              data-testid="explore-cta-workspace"
              className="md-btn md-btn--primary mt-6 inline-flex"
            >
              Explore Ready-to-Work Demo <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>

          {/* Brain Builder — secondary */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.25 }}
            className="rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-6 md:p-8"
          >
            <div className="md-eyebrow">See how intelligence is built</div>
            <h3 className="mt-3 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
              Start from a blank workspace.
            </h3>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--md-fog)]">
              Watch Orkexra learn the business — identity, audience, offers,
              voice, visual identity, market context, and marketing
              preferences — before the first campaign is ever created.
            </p>
            <Link
              to="/demo/brain-builder"
              data-testid="explore-cta-brain-builder"
              className="md-btn md-btn--ghost mt-6 inline-flex"
            >
              See How It Works <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Page shell ─────────────────────────────────────────────── */
export default function LandingPage(): JSX.Element {
  return (
    <div data-testid="landing-page" className="relative min-h-screen text-[var(--md-ivory-0)]">
      <Aurora />
      <PublicNav />
      <main>
        <Hero />
        <ChooseHowToExplore />
        <WhyExists />
        <LearnYourBusiness />
        <TellUsTheGoal />
        <CampaignFlow />
        <MeetTheDepartment />
        <EverythingWeCreate />
        <ReviewOnlyWhatMatters />
        <OutputShowcase />
        <TrustSection />
        <PublishingDestinations />
        <PublishingSection />
        <AutopilotSection />
        <YourMorning />
        <BuiltForGrowingBrands />
        <FinalCTA />
      </main>
      <PublicFooter />
    </div>
  );
}
